import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { translateLocation } from '../../utils/translationUtils';
import Modal from '../../components/Modal';
import './Studies.css';

const EXAM_COMPONENT_OPTIONS = [
  'Kennistentamen',
  'Onderzoeksverslag/ voorstel',
  'Beroepsproduct',
  'Adviesrapport',
  'Reflectieverslag/ portfolio',
  'Schriftelijke opdracht',
  'Presentatievorm'
];

const WEIGHT_OPTIONS = Array.from({ length: 11 }, (_, index) => index * 10);

const Studies = () => {
  const { t } = useTranslation();
  const {
    data,
    addStudy,
    updateStudy,
    deleteStudy,
    addStudyYear,
    updateStudyYear,
    deleteStudyYear,
    addCourse,
    addCourseModule,
    updateCourseModule,
    addCourseExamPoint,
    updateCourse,
    deleteCourse,
    deleteCourseModule,
    deleteCourseExamPoint,
    updateCourseExamPoint,
    instructors,
    getFilteredData
  } = useData();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext();

  const [expandedStudy, setExpandedStudy] = useState(null);
  const [expandedYear, setExpandedYear] = useState(null);
  const [expandedVak, setExpandedVak] = useState(null);
  const [showAddStudy, setShowAddStudy] = useState(false);
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [showEditYearModal, setShowEditYearModal] = useState(false);
  const [showAddVakModal, setShowAddVakModal] = useState(false);
  const [showAddVakModuleModal, setShowAddVakModuleModal] = useState(false);
  const [showAddVakExamModal, setShowAddVakExamModal] = useState(false);
  const [targetStudyForYear, setTargetStudyForYear] = useState(null);
  const [targetYearForVak, setTargetYearForVak] = useState({ studyId: null, yearId: null, location: 'paramaribo' });
  const [targetVakForModule, setTargetVakForModule] = useState({ courseId: null, courseCode: '' });
  const [targetVakForExam, setTargetVakForExam] = useState({ courseId: null, courseName: '' });
  const [editingVakModule, setEditingVakModule] = useState(null);
  const [showEditVakModuleModal, setShowEditVakModuleModal] = useState(false);
  const [editingVak, setEditingVak] = useState(null);
  const [showEditVakModal, setShowEditVakModal] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null);
  const [editingStudyYear, setEditingStudyYear] = useState(null);
  const [vakFormErrors, setVakFormErrors] = useState({});
  const [editingExamPoint, setEditingExamPoint] = useState(null);
  const [showEditExamPointModal, setShowEditExamPointModal] = useState(false);

  const toIsoDate = (value) => {
    if (!value) return '';
    const normalized = String(value).trim();

    const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return normalized;

    const dmyMatch = normalized.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmyMatch) {
      const [, dd, mm, yyyy] = dmyMatch;
      return `${yyyy}-${mm}-${dd}`;
    }

    return '';
  };

  const toDisplayDate = (isoValue) => {
    if (!isoValue) return '';
    const match = String(isoValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    const [, yyyy, mm, dd] = match;
    return `${dd}-${mm}-${yyyy}`;
  };

  const [newStudy, setNewStudy] = useState({ name: '', description: '', level: '', duration: '', location: 'paramaribo', cohortId: '' });
  const [newStudyYear, setNewStudyYear] = useState({ yearNumber: 1, name: '' });
  const [newVak, setNewVak] = useState({
    name: '',
    code: '',
    description: '',
    courseType: 'instructor-led',
    instructorId: '',
    startDate: '',
    endDate: '',
    maxStudents: '',
    ects: '',
    moduleName: '',
    moduleDescription: '',
    moduleEcts: ''
  });
  const [newVakModule, setNewVakModule] = useState({ name: '', code: 'MOD1', description: '' });
  const [newVakExamPoint, setNewVakExamPoint] = useState({ name: EXAM_COMPONENT_OPTIONS[0], weight: '10', maxScore: '10', passScore: '5.5', description: '' });

  const filteredData = selectedLocation === 'all'
    ? { studies: data?.studies || [] }
    : getFilteredData ? getFilteredData(user.role, selectedLocation) : { studies: data?.studies || [] };

  const visibleCohorts = (data?.cohorts || []).filter((cohort) => {
    if (selectedLocation && selectedLocation !== 'all') {
      return String(cohort.location || '').toLowerCase() === String(selectedLocation).toLowerCase();
    }
    if (user.role === 'manager') {
      return String(cohort.location || '').toLowerCase() === String(user.location || '').toLowerCase();
    }
    return true;
  });

  const visibleInstructors = (instructors || []).filter((inst) => {
    if (selectedLocation && selectedLocation !== 'all') {
      return String(inst.location || '').toLowerCase() === String(selectedLocation).toLowerCase();
    }
    if (user.role === 'manager') {
      return String(inst.location || '').toLowerCase() === String(user.location || '').toLowerCase();
    }
    return true;
  });

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const handleAddStudy = async (e) => {
    e.preventDefault();
    if (!newStudy.name || !newStudy.description) return;

    await addStudy({
      ...newStudy,
      duration: Number(newStudy.duration || 0),
      cohortIds: newStudy.cohortId ? [newStudy.cohortId] : []
    });

    setNewStudy({ name: '', description: '', level: '', duration: '', location: 'paramaribo', cohortId: '' });
    setShowAddStudy(false);
  };

  const handleUpdateStudy = async (e) => {
    e.preventDefault();
    if (!editingStudy?.id) return;

    await updateStudy(editingStudy.id, {
      name: editingStudy.name,
      description: editingStudy.description,
      isActive: editingStudy.isActive,
      cohortIds: editingStudy.cohortId ? [editingStudy.cohortId] : []
    });

    setEditingStudy(null);
  };

  const openAddStudyYearModal = (studyId) => {
    const study = filteredData.studies.find((s) => s.id === studyId);
    const yearCount = (study?.studyYears?.length || 0) + 1;
    setTargetStudyForYear(studyId);
    setNewStudyYear({ yearNumber: yearCount, name: `Study Year ${yearCount}` });
    setShowAddYearModal(true);
  };

  const handleAddStudyYear = async (e) => {
    e.preventDefault();
    if (!targetStudyForYear || !newStudyYear.yearNumber) return;

    await addStudyYear(targetStudyForYear, {
      yearNumber: Number(newStudyYear.yearNumber),
      name: newStudyYear.name || `Study Year ${newStudyYear.yearNumber}`
    });

    setShowAddYearModal(false);
    setTargetStudyForYear(null);
  };

  const openEditStudyYearModal = (study, year) => {
    setEditingStudyYear({
      studyId: study.id,
      yearId: year.id,
      yearNumber: Number(year.yearNumber || year.year || 1),
      name: year.name || `Study Year ${year.yearNumber || year.year || 1}`
    });
    setShowEditYearModal(true);
  };

  const handleUpdateStudyYear = async (e) => {
    e.preventDefault();
    if (!editingStudyYear?.studyId || !editingStudyYear?.yearId || !editingStudyYear?.yearNumber || !editingStudyYear?.name) return;

    try {
      await updateStudyYear(editingStudyYear.studyId, editingStudyYear.yearId, {
        yearNumber: Number(editingStudyYear.yearNumber),
        name: editingStudyYear.name
      });
      setShowEditYearModal(false);
      setEditingStudyYear(null);
    } catch (error) {
      window.alert(error.message || 'Studiejaar bewerken mislukt');
    }
  };

  const handleDeleteStudyYear = async (studyId, yearId) => {
    if (!window.confirm('Weet u zeker dat u dit studiejaar wilt verwijderen?')) return;

    try {
      await deleteStudyYear(studyId, yearId);
      if (expandedYear === yearId) {
        setExpandedYear(null);
      }
    } catch (error) {
      window.alert(error.message || 'Studiejaar verwijderen mislukt');
    }
  };

  const openAddVakModal = (studyId, yearId, location) => {
    setTargetYearForVak({ studyId, yearId, location: location || 'paramaribo' });
    setNewVak({
      name: '',
      code: '',
      description: '',
      courseType: 'instructor-led',
      instructorId: '',
      startDate: '',
      endDate: '',
      maxStudents: '',
      ects: '',
      moduleName: '',
      moduleDescription: '',
      moduleEcts: ''
    });
    setShowAddVakModal(true);
  };

  const openAddVakModuleModal = (vak) => {
    const nextIndex = (vak.modules || []).length + 1;
    setTargetVakForModule({ courseId: vak.id, courseCode: vak.code });
    setNewVakModule({ name: '', code: `MOD${nextIndex}`, description: '' });
    setShowAddVakModuleModal(true);
  };

  const openAddVakExamModal = (vak) => {
    setTargetVakForExam({ courseId: vak.id, courseName: vak.name });
    setNewVakExamPoint({ name: EXAM_COMPONENT_OPTIONS[0], weight: '10', maxScore: '10', passScore: '5.5', description: '' });
    setShowAddVakExamModal(true);
  };

  const openEditVakModal = (vak) => {
    setEditingVak({
      id: vak.id,
      name: vak.name || '',
      code: vak.code || '',
      description: vak.description || '',
      courseType: vak.courseType || 'instructor-led',
      instructorId: vak.instructor?.id || vak.instructor?._id || '',
      startDate: vak.startDate ? toDisplayDate(String(vak.startDate).slice(0, 10)) : '',
      endDate: vak.endDate ? toDisplayDate(String(vak.endDate).slice(0, 10)) : '',
      maxStudents: vak.maxStudents || '',
      ects: vak.ects || 0,
      location: vak.location || 'paramaribo'
    });
    setVakFormErrors({});
    setShowEditVakModal(true);
  };

  const openEditVakModuleModal = (studyId, yearId, moduleDoc) => {
    setEditingVakModule({
      studyId,
      yearId,
      moduleId: moduleDoc.id || moduleDoc._id,
      name: moduleDoc.name || '',
      code: moduleDoc.code || '',
      description: moduleDoc.description || ''
    });
    setShowEditVakModuleModal(true);
  };

  const handleAddVak = async (e) => {
    e.preventDefault();
    const startDateIso = toIsoDate(newVak.startDate);
    const endDateIso = toIsoDate(newVak.endDate);
    const errors = {};

    if (!targetYearForVak.yearId) errors.studyYear = 'Studiejaar is verplicht';
    if (!newVak.name) errors.name = 'Vaknaam is verplicht';
    if (!newVak.code) errors.code = 'Vakcode is verplicht';
    if (!startDateIso) errors.startDate = 'Gebruik formaat dd-mm-jjjj';
    if (!endDateIso) errors.endDate = 'Gebruik formaat dd-mm-jjjj';
    if (startDateIso && endDateIso && new Date(startDateIso) > new Date(endDateIso)) {
      errors.endDate = 'Einddatum moet na startdatum liggen';
    }

    setVakFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const normalizedCode = newVak.code.toUpperCase().trim();

    try {
      await addCourse({
        name: newVak.name,
        code: normalizedCode,
        description: newVak.description,
        instructorId: newVak.instructorId || null,
        ects: newVak.ects ? Number(newVak.ects) : 0,
        startDate: startDateIso,
        endDate: endDateIso,
        location: targetYearForVak.location,
        maxStudents: newVak.maxStudents ? Number(newVak.maxStudents) : undefined,
        courseType: newVak.courseType,
        studyYearId: targetYearForVak.yearId,
        examPoints: []
      });

      setVakFormErrors({});
      setShowAddVakModal(false);
    } catch (error) {
      window.alert(error.message || 'Vak aanmaken mislukt');
    }
  };

  const handleUpdateVak = async (e) => {
    e.preventDefault();
    const startDateIso = toIsoDate(editingVak?.startDate);
    const endDateIso = toIsoDate(editingVak?.endDate);
    const errors = {};

    if (!editingVak?.id || !editingVak.name || !editingVak.code) {
      errors.base = 'Vaknaam en vakcode zijn verplicht';
    }
    if (!startDateIso) errors.startDate = 'Gebruik formaat dd-mm-jjjj';
    if (!endDateIso) errors.endDate = 'Gebruik formaat dd-mm-jjjj';
    if (startDateIso && endDateIso && new Date(startDateIso) > new Date(endDateIso)) {
      errors.endDate = 'Einddatum moet na startdatum liggen';
    }

    setVakFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      await updateCourse(editingVak.id, {
        name: editingVak.name,
        code: editingVak.code,
        description: editingVak.description,
        instructorId: editingVak.instructorId || null,
        ects: editingVak.ects ? Number(editingVak.ects) : 0,
        startDate: startDateIso,
        endDate: endDateIso,
        location: editingVak.location,
        maxStudents: editingVak.maxStudents ? Number(editingVak.maxStudents) : undefined,
        courseType: editingVak.courseType
      });

      setVakFormErrors({});
      setShowEditVakModal(false);
      setEditingVak(null);
    } catch (error) {
      window.alert(error.message || 'Vak bewerken mislukt');
    }
  };

  const handleAddVakModule = async (e) => {
    e.preventDefault();
    if (!targetVakForModule.courseId || !newVakModule.name) return;

    try {
      await addCourseModule(targetVakForModule.courseId, {
        name: newVakModule.name,
        codeSuffix: newVakModule.code,
        description: newVakModule.description
      });

      setShowAddVakModuleModal(false);
      setTargetVakForModule({ courseId: null, courseCode: '' });
    } catch (error) {
      window.alert(error.message || 'Module toevoegen mislukt');
    }
  };

  const handleUpdateVakModule = async (e) => {
    e.preventDefault();
    if (!editingVakModule?.studyId || !editingVakModule?.yearId || !editingVakModule?.moduleId || !editingVakModule?.name) return;

    try {
      await updateCourseModule(editingVakModule.studyId, editingVakModule.yearId, editingVakModule.moduleId, {
        name: editingVakModule.name,
        code: editingVakModule.code,
        description: editingVakModule.description
      });

      setShowEditVakModuleModal(false);
      setEditingVakModule(null);
    } catch (error) {
      window.alert(error.message || 'Module bewerken mislukt');
    }
  };

  const handleAddVakExamPoint = async (e) => {
    e.preventDefault();
    if (!targetVakForExam.courseId || !newVakExamPoint.name || newVakExamPoint.weight === '') return;

    try {
      await addCourseExamPoint(targetVakForExam.courseId, {
        name: newVakExamPoint.name,
        weight: Number(newVakExamPoint.weight),
        maxScore: Number(newVakExamPoint.maxScore || 10),
        passScore: Number(newVakExamPoint.passScore || 5.5),
        description: newVakExamPoint.description
      });

      setShowAddVakExamModal(false);
      setTargetVakForExam({ courseId: null, courseName: '' });

    } catch (error) {
      window.alert(error.message || 'Examenpunt toevoegen mislukt');
    }
  };

  const handleDeleteVakModule = async (vakId, moduleDoc) => {
    const moduleId = moduleDoc?.id || moduleDoc?._id;
    if (!moduleId) return;
    if (!window.confirm('Weet u zeker dat u deze module wilt verwijderen?')) return;

    try {
      await deleteCourseModule(vakId, moduleId);
    } catch (error) {
      window.alert(error.message || 'Module verwijderen mislukt');
    }
  };

  const handleDeleteExamPoint = async (vakId, index) => {
    if (!window.confirm('Weet u zeker dat u dit examenonderdeel wilt verwijderen?')) return;

    try {
      await deleteCourseExamPoint(vakId, index);
    } catch (error) {
      window.alert(error.message || 'Examenonderdeel verwijderen mislukt');
    }
  };

  const handleOpenEditExamPoint = (vakId, idx, point) => {
    setEditingExamPoint({ courseId: vakId, index: idx, name: point.name, weight: String(point.weight), maxScore: String(point.maxScore ?? 10), passScore: String(point.passScore ?? 5.5), description: point.description || '' });
    setShowEditExamPointModal(true);
  };

  const handleSaveExamPoint = async (e) => {
    e.preventDefault();
    if (!editingExamPoint) return;
    try {
      await updateCourseExamPoint(editingExamPoint.courseId, editingExamPoint.index, {
        name: editingExamPoint.name,
        weight: Number(editingExamPoint.weight),
        maxScore: Number(editingExamPoint.maxScore),
        passScore: Number(editingExamPoint.passScore),
        description: editingExamPoint.description
      });
      setShowEditExamPointModal(false);
      setEditingExamPoint(null);
    } catch (error) {
      window.alert(error.message || 'Examenonderdeel bewerken mislukt');
    }
  };

  const handleDeleteStudy = async (studyId) => {
    if (!window.confirm('Weet u zeker dat u deze studie wilt verwijderen? Alle studiejaren en vakken worden ook verwijderd.')) return;
    try {
      await deleteStudy(studyId);
      if (expandedStudy === studyId) setExpandedStudy(null);
    } catch (error) {
      window.alert(error.message || 'Studie verwijderen mislukt');
    }
  };

  const handleDeleteVak = async (vakId) => {
    if (!window.confirm('Weet u zeker dat u dit vak wilt verwijderen?')) return;

    try {
      await deleteCourse(vakId);
      if (expandedVak === vakId) {
        setExpandedVak(null);
      }
    } catch (error) {
      window.alert(error.message || 'Vak verwijderen mislukt');
    }
  };

  const getYearVakken = (yearId) => {
    return (data.courses || []).filter((course) => String(course.studyYear?.id || course.studyYear || '') === String(yearId));
  };

  const getTotalVakken = (study) => {
    return (study.studyYears || []).reduce((sum, sy) => sum + getYearVakken(sy.id).length, 0);
  };

  const getTotalModules = (study) => {
    const moduleIds = new Set();
    (study.studyYears || []).forEach((sy) => {
      getYearVakken(sy.id).forEach((vak) => {
        (vak.modules || []).forEach((mod) => {
          const id = mod?.id || mod?._id;
          if (id) moduleIds.add(String(id));
        });
      });
    });
    return moduleIds.size;
  };

  const getTotalECTS = (study) => {
    let ects = 0;
    (study.studyYears || []).forEach((sy) => {
      getYearVakken(sy.id).forEach((vak) => {
        ects += Number(vak.ects || 0);
      });
    });
    return ects;
  };

  return (
    <div className="studies-page">
      <div className="page-header">
        <h1>{t('admin.viewHierarchy')}</h1>
        {(user.role === 'admin' || user.role === 'manager') && (
          <button className="btn-primary" onClick={() => setShowAddStudy(true)}>
            {t('study.add')}
          </button>
        )}
      </div>

      <Modal isOpen={showAddStudy} onClose={() => setShowAddStudy(false)} title={t('study.add')}>
        <form onSubmit={handleAddStudy} className="add-form">
          <div className="form-row">
            <input
              type="text"
              placeholder={t('study.name')}
              value={newStudy.name}
              onChange={(e) => setNewStudy({ ...newStudy, name: e.target.value })}
              className="form-input"
              required
            />
            <input
              type="text"
              placeholder={t('study.level')}
              value={newStudy.level}
              onChange={(e) => setNewStudy({ ...newStudy, level: e.target.value })}
              className="form-input"
            />
            <input
              type="number"
              min="1"
              placeholder={t('study.duration')}
              value={newStudy.duration}
              onChange={(e) => setNewStudy({ ...newStudy, duration: e.target.value })}
              className="form-input"
              required
            />
          </div>
          <small className="field-help">Vul hier het aantal jaren in, bijvoorbeeld `4`.</small>
          <div className="form-row">
            <select
              value={newStudy.location}
              onChange={(e) => setNewStudy({ ...newStudy, location: e.target.value })}
              className="form-input"
            >
              <option value="paramaribo">{t('location.paramaribo')}</option>
              <option value="nickerie">{t('location.nickerie')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>Koppel cohort</label>
            <select
              value={newStudy.cohortId || ''}
              onChange={(e) => setNewStudy({ ...newStudy, cohortId: e.target.value })}
              className="form-input"
            >
              <option value="">— Geen cohort —</option>
              {visibleCohorts.map((cohort) => (
                <option key={cohort.id} value={cohort.id}>{cohort.name} ({cohort.code || 'Geen code'})</option>
              ))}
            </select>
          </div>
          <textarea
            placeholder={t('study.description')}
            value={newStudy.description}
            onChange={(e) => setNewStudy({ ...newStudy, description: e.target.value })}
            className="form-textarea"
            required
          />
          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowAddStudy(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>

      <div className="hierarchy-stats">
        <div className="stat-item">
          <span className="stat-value">{filteredData.studies.length}</span>
          <span className="stat-label">{t('admin.studies')}</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{filteredData.studies.reduce((acc, s) => acc + (s.studyYears?.length || 0), 0)}</span>
          <span className="stat-label">{t('admin.studyYears')}</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{filteredData.studies.reduce((acc, s) => acc + getTotalModules(s), 0)}</span>
          <span className="stat-label">{t('admin.modules')}</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{filteredData.studies.reduce((acc, s) => acc + getTotalVakken(s), 0)}</span>
          <span className="stat-label">Vakken</span>
        </div>
      </div>

      <div className="studies-list">
        {filteredData.studies.map((study) => (
          <div key={study.id} className="study-card">
            <div className="study-header" onClick={() => setExpandedStudy(expandedStudy === study.id ? null : study.id)}>
              <div className="study-info">
                <h3>{study.name}</h3>
                <p>{study.description}</p>
                <div className="study-meta">
                  <span className="meta-level">{study.level}</span>
                  <span className="meta-duration">{study.duration} jaar</span>
                  <span className="meta-location">{translateLocation(t, study.location)}</span>
                  <span className="meta-ects">{getTotalECTS(study)} ECTS</span>
                </div>
                <div className="study-meta">
                  <span className="meta-level">{getStudyLabel(study)}</span>
                </div>
              </div>
              <div className="study-stats">
                <span>{(study.studyYears?.length || 0)} {t('studyYear.plural')}</span>
                <span>{getTotalVakken(study)} vakken</span>
              </div>
              <div className="study-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {(user.role === 'admin' || user.role === 'manager') && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingStudy({
                        ...study,
                        cohortId: (study.cohorts || [])[0]?.id || (study.cohorts || [])[0]?._id || '',
                        isActive: study.isActive !== false
                      });
                    }}
                  >
                    {t('common.edit')}
                  </button>
                )}
                {user.role === 'admin' && (
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={(e) => { e.stopPropagation(); handleDeleteStudy(study.id); }}
                  >
                    {t('common.delete')}
                  </button>
                )}
                <button className="expand-btn">{expandedStudy === study.id ? '−' : '+'}</button>
              </div>
            </div>

            {expandedStudy === study.id && (
              <div className="study-details">
                <div className="study-years">
                  <h4>{t('admin.studyYears')}</h4>
                  {(study.studyYears?.length || 0) === 0 ? (
                    <p className="no-data">{t('study.noStudyYears')}</p>
                  ) : (
                    (study.studyYears || []).map((sy) => {
                      const yearVakken = getYearVakken(sy.id);
                      return (
                        <div key={sy.id} className="study-year-item">
                          <div className="year-header" onClick={() => setExpandedYear(expandedYear === sy.id ? null : sy.id)}>
                            <div className="year-info">
                              <h5>{sy.name || `${t('studyYear.title')} ${sy.yearNumber || sy.year}`}</h5>
                            </div>
                            <div className="year-stats">
                              <span>{yearVakken.length} vakken</span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {(user.role === 'admin' || user.role === 'manager') && (
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditStudyYearModal(study, sy);
                                  }}
                                >
                                  Bewerken
                                </button>
                              )}
                              {user.role === 'admin' && (
                                <button
                                  type="button"
                                  className="btn-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteStudyYear(study.id, sy.id);
                                  }}
                                >
                                  Verwijderen
                                </button>
                              )}
                              <button className="expand-btn-small">{expandedYear === sy.id ? '−' : '+'}</button>
                            </div>
                          </div>

                          {expandedYear === sy.id && (
                            <div className="modules-list">
                              {yearVakken.map((vak) => (
                                <div key={vak.id} className="module-item">
                                  <div className="module-header">
                                    <div className="module-info">
                                      <span className="module-code">{vak.code}</span>
                                      <span className="module-name">{vak.name}</span>
                                    </div>
                                    <div className="module-meta">
                                      <span className="module-ects">{vak.ects || 0} ECTS</span>
                                      <span>{vak.courseType === 'self-paced' ? t('course.selfPaced') : t('course.instructorLed')}</span>
                                      <span>Docent: {vak.instructor?.name || 'Niet toegewezen'}</span>
                                      {(user.role === 'admin' || user.role === 'manager') && (
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          onClick={() => openEditVakModal(vak)}
                                        >
                                          Vak bewerken
                                        </button>
                                      )}
                                      {(user.role === 'admin' || user.role === 'manager') && (
                                        <button
                                          type="button"
                                          className="btn-secondary"
                                          onClick={() => handleDeleteVak(vak.id)}
                                        >
                                          Vak verwijderen
                                        </button>
                                      )}
                                      <button type="button" className="btn-secondary" onClick={() => setExpandedVak(expandedVak === vak.id ? null : vak.id)}>
                                        {expandedVak === vak.id ? 'Modules verbergen' : 'Modules tonen'}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="course-details">
                                    <span>{new Date(vak.startDate).toLocaleDateString()} - {new Date(vak.endDate).toLocaleDateString()}</span>
                                    <span>{translateLocation(t, vak.location)}</span>
                                  </div>
                                  <div className="course-details">
                                    <span><strong>Modules:</strong> {(vak.modules || []).length}</span>
                                    <span><strong>Examenpunten:</strong> {(vak.examPoints || []).length}</span>
                                  </div>
                                  {expandedVak === vak.id && (
                                    <>
                                      {(vak.modules || []).length > 0 && (
                                        <div className="course-details">
                                          {(vak.modules || []).map((mod) => (
                                            <span key={mod.id || mod._id}>
                                              <strong>{mod.code}</strong> - {mod.name}
                                              {(user.role === 'admin' || user.role === 'manager') && (
                                                <>
                                                  <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    style={{ marginLeft: '8px', padding: '4px 8px' }}
                                                    onClick={() => openEditVakModuleModal(study.id, sy.id, mod)}
                                                  >
                                                    Bewerken
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    style={{ marginLeft: '6px', padding: '4px 8px' }}
                                                    onClick={() => handleDeleteVakModule(vak.id, mod)}
                                                  >
                                                    Verwijderen
                                                  </button>
                                                </>
                                              )}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      {(vak.modules || []).length === 0 && (
                                        <div className="course-details">
                                          <span><strong>Modules:</strong> nog niet ingesteld</span>
                                        </div>
                                      )}
                                      {(user.role === 'admin' || user.role === 'manager') && (
                                        <div className="form-actions" style={{ marginTop: '8px' }}>
                                          <button type="button" className="btn-small" onClick={() => openAddVakModuleModal(vak)}>+ Module toevoegen</button>
                                          <button type="button" className="btn-small" onClick={() => openAddVakExamModal(vak)}>+ Examenpunt toevoegen</button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {(vak.examPoints || []).length > 0 && (
                                    <div className="course-details">
                                      {(vak.examPoints || []).map((point, idx) => (
                                        <span key={`${vak.id}-ep-${idx}`}>
                                          <strong>{point.name}</strong>: {point.weight}% (min {point.passScore}/{point.maxScore})
                                          {(user.role === 'admin' || user.role === 'manager') && (
                                            <>
                                              <button
                                                type="button"
                                                className="btn-secondary"
                                                style={{ marginLeft: '8px', padding: '4px 8px' }}
                                                onClick={() => handleOpenEditExamPoint(vak.id, idx, point)}
                                              >
                                                Bewerken
                                              </button>
                                              <button
                                                type="button"
                                                className="btn-secondary"
                                                style={{ marginLeft: '4px', padding: '4px 8px' }}
                                                onClick={() => handleDeleteExamPoint(vak.id, idx)}
                                              >
                                                Verwijderen
                                              </button>
                                            </>
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {(vak.examPoints || []).length === 0 && (
                                    <div className="course-details">
                                      <span><strong>Examenpunten:</strong> nog niet ingesteld</span>
                                    </div>
                                  )}
                                  <div className="course-details">
                                    <span><strong>Label:</strong> ECTS hoort bij het vak als geheel.</span>
                                  </div>
                                </div>
                              ))}

                              {yearVakken.length === 0 && (
                                <p className="no-data">Nog geen vakken toegevoegd.</p>
                              )}

                              {(user.role === 'admin' || user.role === 'manager') && (
                                <button
                                  type="button"
                                  className="btn-small"
                                  onClick={() => openAddVakModal(study.id, sy.id, study.location)}
                                >
                                  + Vak toevoegen
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}

                  {(user.role === 'admin' || user.role === 'manager') && (
                    <button className="btn-small" onClick={() => openAddStudyYearModal(study.id)}>
                      + {t('studyYear.add')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <Modal isOpen={showAddYearModal} onClose={() => setShowAddYearModal(false)} title={t('studyYear.add')} size="sm">
        <form onSubmit={handleAddStudyYear} className="add-form">
          <div className="form-row">
            <input
              type="number"
              min="1"
              value={newStudyYear.yearNumber}
              onChange={(e) => setNewStudyYear((prev) => ({ ...prev, yearNumber: e.target.value }))}
              className="form-input"
              placeholder={t('study.yearNumber')}
              required
            />
            <input
              type="text"
              value={newStudyYear.name}
              onChange={(e) => setNewStudyYear((prev) => ({ ...prev, name: e.target.value }))}
              className="form-input"
              placeholder={t('studyYear.title')}
              required
            />
          </div>
          <small className="field-help">Gebruik opeenvolgende jaarnummers: 1, 2, 3...</small>
          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowAddYearModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showEditYearModal} onClose={() => { setShowEditYearModal(false); setEditingStudyYear(null); }} title="Studiejaar bewerken" size="sm">
        {editingStudyYear && (
          <form onSubmit={handleUpdateStudyYear} className="add-form">
            <div className="form-row">
              <input
                type="number"
                min="1"
                value={editingStudyYear.yearNumber}
                onChange={(e) => setEditingStudyYear((prev) => ({ ...prev, yearNumber: e.target.value }))}
                className="form-input"
                placeholder={t('study.yearNumber')}
                required
              />
              <input
                type="text"
                value={editingStudyYear.name}
                onChange={(e) => setEditingStudyYear((prev) => ({ ...prev, name: e.target.value }))}
                className="form-input"
                placeholder={t('studyYear.title')}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => { setShowEditYearModal(false); setEditingStudyYear(null); }}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showAddVakModal} onClose={() => setShowAddVakModal(false)} title="Vak toevoegen" size="sm">
        <form onSubmit={handleAddVak} className="add-form">
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Vaknaam</label>
              <input
                type="text"
                value={newVak.name}
                onChange={(e) => setNewVak((prev) => ({ ...prev, name: e.target.value }))}
                className="form-input"
                placeholder="Bijv. Inleiding Informatica"
                required
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Vakcode</label>
              <input
                type="text"
                value={newVak.code}
                onChange={(e) => setNewVak((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="form-input"
                placeholder="Bijv. INF101"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Startdatum (dd-mm-jjjj)</label>
              <input
                type="text"
                value={newVak.startDate}
                onChange={(e) => setNewVak((prev) => ({ ...prev, startDate: e.target.value }))}
                className="form-input"
                placeholder="01-09-2026"
                required
              />
              {vakFormErrors.startDate && <small className="field-help error-help">{vakFormErrors.startDate}</small>}
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Einddatum (dd-mm-jjjj)</label>
              <input
                type="text"
                value={newVak.endDate}
                onChange={(e) => setNewVak((prev) => ({ ...prev, endDate: e.target.value }))}
                className="form-input"
                placeholder="31-01-2027"
                required
              />
              {vakFormErrors.endDate && <small className="field-help error-help">{vakFormErrors.endDate}</small>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Type (college/zelfstudie)</label>
              <select
                className="form-input"
                value={newVak.courseType}
                onChange={(e) => setNewVak((prev) => ({ ...prev, courseType: e.target.value }))}
              >
                <option value="instructor-led">College</option>
                <option value="self-paced">{t('course.selfPaced')}</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Docent</label>
              <select
                className="form-input"
                value={newVak.instructorId}
                onChange={(e) => setNewVak((prev) => ({ ...prev, instructorId: e.target.value }))}
              >
                <option value="">-- Geen docent --</option>
                {visibleInstructors.map((inst) => (
                  <option key={inst.id || inst._id} value={inst.id || inst._id}>
                    {inst.name}
                  </option>
                ))}
              </select>
              {vakFormErrors.instructorId && <small className="field-help error-help">{vakFormErrors.instructorId}</small>}
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Wijze (fysiek/online)</label>
              <select
                className="form-input"
                value={targetYearForVak.location}
                onChange={(e) => setTargetYearForVak((prev) => ({ ...prev, location: e.target.value }))}
              >
                <option value="paramaribo">Fysiek</option>
                <option value="nickerie">Online</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>ECTS (per vak)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={newVak.ects}
                onChange={(e) => setNewVak((prev) => ({ ...prev, ects: e.target.value }))}
                className="form-input"
                placeholder="Bijv. 5"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Max studenten (optioneel)</label>
              <input
                type="number"
                min="1"
                value={newVak.maxStudents}
                onChange={(e) => setNewVak((prev) => ({ ...prev, maxStudents: e.target.value }))}
                className="form-input"
                placeholder="Bijv. 30"
              />
            </div>
          </div>
          <small className="field-help">Docent koppelen is optioneel bij aanmaken en kan later altijd nog.</small>
          <small className="field-help">ECTS staat leeg als standaard zodat admin/manager dit bewust per vak invult.</small>

          <small className="field-help">Modules voeg je na het opslaan van het vak toe onder het vak zelf (dropdown: Modules tonen).</small>

          <small className="field-help">Examenpunten voeg je na het opslaan van het vak toe via de knop `+ Examenpunt toevoegen` onder het vak.</small>

          <textarea
            value={newVak.description}
            onChange={(e) => setNewVak((prev) => ({ ...prev, description: e.target.value }))}
            className="form-textarea"
            placeholder="Extra notitie (optioneel)"
          />

          <small className="field-help">Een vak kan meerdere modules en meerdere examenpunten bevatten. Deze kun je als admin/manager vrij bepalen.</small>
          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowAddVakModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showAddVakModuleModal} onClose={() => setShowAddVakModuleModal(false)} title="Module toevoegen onder vak" size="sm">
        <form onSubmit={handleAddVakModule} className="add-form">
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Modulenaam</label>
              <input
                type="text"
                value={newVakModule.name}
                onChange={(e) => setNewVakModule((prev) => ({ ...prev, name: e.target.value }))}
                className="form-input"
                placeholder="Bijv. Inleiding Leiderschap"
                required
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Modulecode</label>
              <input
                type="text"
                value={newVakModule.code}
                onChange={(e) => setNewVakModule((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="form-input"
                placeholder="Bijv. AR-101"
              />
            </div>
          </div>

          <textarea
            value={newVakModule.description}
            onChange={(e) => setNewVakModule((prev) => ({ ...prev, description: e.target.value }))}
            className="form-textarea"
            placeholder="Modulebeschrijving (optioneel)"
          />

          <small className="field-help">De ingevoerde modulecode wordt exact opgeslagen (zonder automatische vakcode-prefix).</small>
          <small className="field-help">Modules hebben geen ECTS; ECTS wordt alleen op vakniveau ingevuld.</small>

          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowAddVakModuleModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showAddVakExamModal} onClose={() => setShowAddVakExamModal(false)} title="Examenpunt toevoegen onder vak" size="sm">
        <form onSubmit={handleAddVakExamPoint} className="add-form">
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Naam examenpunt</label>
              <select
                value={newVakExamPoint.name}
                onChange={(e) => setNewVakExamPoint((prev) => ({ ...prev, name: e.target.value }))}
                className="form-input"
              >
                {EXAM_COMPONENT_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Weging (%)</label>
              <select
                value={newVakExamPoint.weight}
                onChange={(e) => setNewVakExamPoint((prev) => ({ ...prev, weight: e.target.value }))}
                className="form-input"
              >
                {WEIGHT_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}%</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Max score</label>
              <input
                type="number"
                min="1"
                step="0.1"
                value={newVakExamPoint.maxScore}
                onChange={(e) => setNewVakExamPoint((prev) => ({ ...prev, maxScore: e.target.value }))}
                className="form-input"
                placeholder="Bijv. 10"
                required
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Minimale score</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={newVakExamPoint.passScore}
                onChange={(e) => setNewVakExamPoint((prev) => ({ ...prev, passScore: e.target.value }))}
                className="form-input"
                placeholder="Bijv. 5.5"
                required
              />
            </div>
          </div>

          <textarea
            value={newVakExamPoint.description}
            onChange={(e) => setNewVakExamPoint((prev) => ({ ...prev, description: e.target.value }))}
            className="form-textarea"
            placeholder="Beschrijving examenpunt (optioneel)"
          />

          <small className="field-help">Je voegt dit examenpunt toe aan vak: {targetVakForExam.courseName || '-'}</small>

          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowAddVakExamModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showEditVakModuleModal} onClose={() => setShowEditVakModuleModal(false)} title="Module bewerken" size="sm">
        {editingVakModule && (
          <form onSubmit={handleUpdateVakModule} className="add-form">
            <div className="form-group">
              <label>Modulenaam</label>
              <input
                type="text"
                value={editingVakModule.name}
                onChange={(e) => setEditingVakModule((prev) => ({ ...prev, name: e.target.value }))}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label>Modulecode</label>
              <input
                type="text"
                value={editingVakModule.code}
                onChange={(e) => setEditingVakModule((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label>Beschrijving</label>
              <textarea
                value={editingVakModule.description}
                onChange={(e) => setEditingVakModule((prev) => ({ ...prev, description: e.target.value }))}
                className="form-textarea"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowEditVakModuleModal(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showEditVakModal} onClose={() => setShowEditVakModal(false)} title="Vak bewerken" size="sm">
        {editingVak && (
          <form onSubmit={handleUpdateVak} className="add-form">
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Vaknaam</label>
                <input
                  type="text"
                  value={editingVak.name}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, name: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Vakcode</label>
                <input
                  type="text"
                  value={editingVak.code}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Startdatum (dd-mm-jjjj)</label>
                <input
                  type="text"
                  value={editingVak.startDate}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="form-input"
                  placeholder="01-09-2026"
                  required
                />
                {vakFormErrors.startDate && <small className="field-help error-help">{vakFormErrors.startDate}</small>}
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Einddatum (dd-mm-jjjj)</label>
                <input
                  type="text"
                  value={editingVak.endDate}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="form-input"
                  placeholder="31-01-2027"
                  required
                />
                {vakFormErrors.endDate && <small className="field-help error-help">{vakFormErrors.endDate}</small>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Type (college/zelfstudie)</label>
                <select
                  value={editingVak.courseType}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, courseType: e.target.value }))}
                  className="form-input"
                >
                  <option value="instructor-led">College</option>
                  <option value="self-paced">{t('course.selfPaced')}</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Docent</label>
                <select
                  value={editingVak.instructorId || ''}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, instructorId: e.target.value }))}
                  className="form-input"
                >
                  <option value="">-- Geen docent --</option>
                  {visibleInstructors.map((inst) => (
                    <option key={inst.id || inst._id} value={inst.id || inst._id}>
                      {inst.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Wijze (fysiek/online)</label>
                <select
                  value={editingVak.location}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, location: e.target.value }))}
                  className="form-input"
                >
                  <option value="paramaribo">Fysiek</option>
                  <option value="nickerie">Online</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>ECTS</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editingVak.ects}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, ects: e.target.value }))}
                  className="form-input"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Max studenten</label>
                <input
                  type="number"
                  min="1"
                  value={editingVak.maxStudents}
                  onChange={(e) => setEditingVak((prev) => ({ ...prev, maxStudents: e.target.value }))}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Beschrijving</label>
              <textarea
                value={editingVak.description}
                onChange={(e) => setEditingVak((prev) => ({ ...prev, description: e.target.value }))}
                className="form-textarea"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setShowEditVakModal(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={showEditExamPointModal} onClose={() => { setShowEditExamPointModal(false); setEditingExamPoint(null); }} title="Examenpunt bewerken" size="sm">
        {editingExamPoint && (
          <form onSubmit={handleSaveExamPoint} className="add-form">
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Naam examenpunt</label>
                <select
                  value={editingExamPoint.name}
                  onChange={(e) => setEditingExamPoint((prev) => ({ ...prev, name: e.target.value }))}
                  className="form-input"
                >
                  {EXAM_COMPONENT_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Weging (%)</label>
                <select
                  value={editingExamPoint.weight}
                  onChange={(e) => setEditingExamPoint((prev) => ({ ...prev, weight: e.target.value }))}
                  className="form-input"
                >
                  {WEIGHT_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}%</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label>Max score</label>
                <input
                  type="number"
                  min="1"
                  step="0.1"
                  value={editingExamPoint.maxScore}
                  onChange={(e) => setEditingExamPoint((prev) => ({ ...prev, maxScore: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Minimale score</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={editingExamPoint.passScore}
                  onChange={(e) => setEditingExamPoint((prev) => ({ ...prev, passScore: e.target.value }))}
                  className="form-input"
                  required
                />
              </div>
            </div>
            <textarea
              value={editingExamPoint.description}
              onChange={(e) => setEditingExamPoint((prev) => ({ ...prev, description: e.target.value }))}
              className="form-textarea"
              placeholder="Beschrijving (optioneel)"
            />
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => { setShowEditExamPointModal(false); setEditingExamPoint(null); }}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>

      <Modal isOpen={!!editingStudy} onClose={() => setEditingStudy(null)} title={t('common.edit')}>
        {editingStudy && (
          <form onSubmit={handleUpdateStudy} className="add-form">
            <div className="form-row">
              <input
                type="text"
                value={editingStudy.name || ''}
                onChange={(e) => setEditingStudy((prev) => ({ ...prev, name: e.target.value }))}
                className="form-input"
                placeholder={t('study.name')}
                required
              />
            </div>
            <textarea
              value={editingStudy.description || ''}
              onChange={(e) => setEditingStudy((prev) => ({ ...prev, description: e.target.value }))}
              className="form-textarea"
              placeholder={t('study.description')}
            />
            <div className="form-group">
              <label>Koppel cohort</label>
              <select
                value={editingStudy.cohortId || ''}
                onChange={(e) => setEditingStudy((prev) => ({ ...prev, cohortId: e.target.value }))}
                className="form-input"
              >
                <option value="">— Geen cohort —</option>
                {visibleCohorts.map((cohort) => (
                  <option key={cohort.id} value={cohort.id}>{cohort.name} ({cohort.code || 'Geen code'})</option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary">{t('common.save')}</button>
              <button type="button" className="btn-secondary" onClick={() => setEditingStudy(null)}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Studies;
